import { supabase } from '@/lib/supabase';

/** Canonical project workflow stages (not lifecycle status). */
export const CANONICAL_PROJECT_STAGES = [
  'Planning',
  'Design',
  'Consent',
  'Construction',
  'Handover',
  'Completed',
] as const;

export type CanonicalProjectStage = (typeof CANONICAL_PROJECT_STAGES)[number];

const LIFECYCLE_STATUS_VALUES = new Set(['active', 'planning', 'completed', 'complete']);

/** True when value is project lifecycle status, not a workflow stage name. */
export function isProjectLifecycleStatus(value: string | null | undefined): boolean {
  if (!value) return false;
  return LIFECYCLE_STATUS_VALUES.has(value.trim().toLowerCase());
}

/**
 * Normalize stored current_stage — rejects lifecycle status values wrongly written to current_stage.
 */
export function normalizeStoredProjectStage(value: string | null | undefined): string | null {
  if (!value || !value.trim()) return null;
  const trimmed = value.trim();
  if (isProjectLifecycleStatus(trimmed)) return null;
  if ((CANONICAL_PROJECT_STAGES as readonly string[]).includes(trimmed)) return trimmed;
  return trimmed;
}

/** Persist manual project workflow stage without changing lifecycle status (active/planning/completed). */
export async function updateProjectCurrentStage(
  projectId: string,
  newStage: string
): Promise<{ ok: boolean; error?: string }> {
  if (!projectId || !newStage.trim()) {
    return { ok: false, error: 'Missing project or stage.' };
  }

  const { error: projErr } = await supabase
    .from('jphph_projects')
    .update({
      current_stage: newStage.trim(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', projectId);

  if (projErr) {
    return { ok: false, error: projErr.message };
  }

  try {
    await supabase
      .from('jphph_project_stages')
      .update({ is_current: false })
      .eq('project_id', projectId);
    await supabase
      .from('jphph_project_stages')
      .update({ is_current: true })
      .eq('project_id', projectId)
      .eq('stage_name', newStage.trim());
  } catch {
    /* optional — dashboard may fall back to current_stage column */
  }

  try {
    window.dispatchEvent(
      new CustomEvent('__jphph_stage_changed__', {
        detail: { projectId, newStage: newStage.trim() },
      })
    );
    window.dispatchEvent(
      new CustomEvent('__jphph_schedule_sync__', {
        detail: {
          projectId,
          isComplete: false,
          timestamp: new Date().toISOString(),
          source: 'updateProjectCurrentStage',
        },
      })
    );
  } catch {
    /* noop */
  }

  return { ok: true };
}
